import React from "react";
import { Badge, Btn, Card, EmptyState, Loading, Select } from "../../ForBrands/ui/BrandUI";
import Icon from "../../ForBrands/ui/icons";
import {
  STATUS_FINAL,
  editedText,
  gameDateText,
  homeAwayLabel,
  plural,
  playersText,
  resultOf,
  scoreText,
} from "./gameStatsModel";

// A season of games: the season picker, "New game", and one row per game
// (date, opponent, home or away, the score with W, L or T, draft or final,
// how many players have a line, who saved it last). A row opens the game.

const POSITIVE = "#1f9d55";
const CRITICAL = "#e2241a";

const RESULT = {
  W: { tone: "outline", dot: POSITIVE, title: "Win" },
  L: { tone: "outline", dot: CRITICAL, title: "Loss" },
  T: { tone: "soft", dot: null, title: "Tie" },
};

export const ResultBadge = ({ result }) => {
  const meta = RESULT[result];
  if (!meta) return null;
  return (
    <span title={meta.title} className="inline-flex">
      <Badge tone={meta.tone} dot={meta.dot || undefined}>
        {result}
      </Badge>
    </span>
  );
};

export const GameStatusBadge = ({ status }) =>
  status === STATUS_FINAL ? (
    <Badge tone="outline" dot={POSITIVE}>
      Final
    </Badge>
  ) : (
    <Badge tone="attention">Draft</Badge>
  );

const TH = "whitespace-nowrap border-b border-bm-border px-3 py-2.5 bm-label-xs uppercase tracking-[0.06em] text-bm-muted";
const TD = "px-3 py-2.5 bm-body-m text-bm-muted";
const HEADS = ["Date", "Opponent", "Home or away", "Score", "Status", "Players", "Last edited"];

const GameList = ({
  games = [],
  season = null,
  seasons = [],
  onSeasonChange,
  onOpen,
  onNew,
  canEdit = false,
  rosterEmpty = false,
  loading = false,
  blocked = "",
}) => {
  const options = !season || seasons.includes(season) ? seasons : [season, ...seasons];
  // Newest first, as the server sends them (a stable sort keeps its order
  // within a day).
  const shown = [...games].sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
  let body;
  if (loading) body = <Loading label="Loading games..." />;
  else if (blocked) body = <EmptyState className="m-5">{blocked}</EmptyState>;
  else if (rosterEmpty) body = <EmptyState className="m-5">Add players to the roster first.</EmptyState>;
  else if (!games.length) {
    const none = season ? `No games entered for ${season} yet.` : "No games entered yet.";
    body = <EmptyState className="m-5">{canEdit ? `${none} Add the first one.` : none}</EmptyState>;
  } else {
    body = (
      <div className="overflow-x-auto">
        <table className="w-full text-left" aria-label={season ? `Games, ${season}` : "Games"}>
          <thead className="bg-bm-subtle">
            <tr>
              {HEADS.map((h) => (
                <th key={h} scope="col" className={TH}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((game) => {
              const result = resultOf(game);
              const date = gameDateText(game.date);
              return (
                <tr
                  key={game.game_id}
                  onClick={() => onOpen?.(game)}
                  className="cursor-pointer border-b border-bm-border bg-white transition-colors last:border-0 hover:bg-bm-subtle"
                >
                  <td className={`${TD} whitespace-nowrap text-bm-ink`}>{date}</td>
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen?.(game);
                      }}
                      aria-label={`Open the game against ${game.opponent} on ${date}`}
                      className="text-left bm-label-l text-bm-ink hover:underline"
                    >
                      {game.opponent}
                    </button>
                    {game.competition && <div className="bm-body-s text-bm-muted">{game.competition}</div>}
                  </td>
                  <td className={TD}>{homeAwayLabel(game.home_away)}</td>
                  <td className={TD}>
                    <span className="inline-flex items-center gap-2">
                      <span className="tabular-nums text-bm-ink">{scoreText(game)}</span>
                      {result && <ResultBadge result={result} />}
                    </span>
                  </td>
                  <td className={TD}>
                    <GameStatusBadge status={game.status} />
                  </td>
                  <td className={`${TD} whitespace-nowrap`}>{playersText(game)}</td>
                  <td className={`${TD} bm-body-s`}>{editedText(game)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <Card padding="p-0" className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-bm-border px-5 py-4">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <h2 className="bm-h-s text-bm-ink">Games</h2>
          {options.length > 0 && (
            <Select aria-label="Season" value={season || ""} onChange={(e) => onSeasonChange?.(e.target.value)} className="!h-9 !w-[124px]">
              {options.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </Select>
          )}
          {!loading && !blocked && <span className="bm-body-s text-bm-muted">{plural(games.length, "game")}</span>}
        </div>
        {canEdit && !blocked && (
          <Btn size="sm" onClick={onNew} disabled={rosterEmpty || loading}>
            <Icon name="plus" size={13} className="mr-1.5" /> New game
          </Btn>
        )}
      </div>
      {body}
    </Card>
  );
};

export default GameList;
